import { useEffect, useState } from "react";
import type { YRoom } from "./useYRoom";

export type SharedQueueEntry<T> = {
  /** Stable queue entry id, generated by `enqueue` unless supplied explicitly. */
  id: string;
  /** App-owned value awaiting work. */
  payload: T;
  /** Local epoch timestamp at which this entry was inserted. */
  enqueuedAt: number;
  /** Peer currently processing the entry, if any. */
  claimedBy: string | null;
  /** Epoch timestamp after which another peer may take over the claim. */
  claimExpiresAt: number | null;
};

export type SharedQueue<T> = {
  /** Entries in FIFO order. Claimed entries remain visible until acknowledged. */
  entries: SharedQueueEntry<T>[];
  /** Number of entries which may currently be claimed. */
  pending: number;
  /** Add an entry and return it, or null if its supplied id already exists. */
  enqueue: (payload: T, opts?: { id?: string }) => SharedQueueEntry<T> | null;
  /** Claim the oldest available entry for this peer. */
  claimNext: () => SharedQueueEntry<T> | null;
  /** Complete and remove one of this peer's claimed entries. */
  acknowledge: (id: string) => boolean;
  /** Release one of this peer's claimed entries for another peer to process. */
  release: (id: string) => boolean;
  /** Remove an entry regardless of claimant (for host/moderation flows). */
  remove: (id: string) => boolean;
};

export type SharedQueueOptions = {
  /** How long a worker owns an item before another peer may reclaim it. Default: 60 seconds. */
  claimTtlMs?: number;
  /** Time source; pass `clock.meshNow` to make expiry follow mesh time. */
  now?: () => number;
};

function makeId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `queue-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isAvailable<T>(entry: SharedQueueEntry<T>, now: number): boolean {
  return (
    entry.claimedBy === null ||
    entry.claimExpiresAt === null ||
    entry.claimExpiresAt <= now
  );
}

/**
 * A FIFO work queue backed by `Y.Array`, with soft claims that expire so a
 * disconnected worker cannot strand shared work forever. It intentionally
 * keeps claim/ack explicit: render `entries`, let a worker call `claimNext`,
 * then call `acknowledge` only after its work is complete.
 */
export function useSharedQueue<T>(
  room: YRoom | null,
  key: string,
  opts?: SharedQueueOptions,
): SharedQueue<T> {
  const [, rerender] = useState(0);
  const ttlMs = Math.max(1, opts?.claimTtlMs ?? 60_000);
  const now = opts?.now ?? Date.now;

  useEffect(() => {
    if (!room) return;
    const array = room.doc.getArray<SharedQueueEntry<T>>(key);
    const onChange = () => rerender((version) => version + 1);
    array.observe(onChange);
    return () => array.unobserve(onChange);
  }, [room, key]);

  const array = room ? room.doc.getArray<SharedQueueEntry<T>>(key) : null;
  const entries = array ? array.toArray() : [];
  const currentEntries = () => (array ? array.toArray() : []);
  const indexOf = (id: string) => currentEntries().findIndex((entry) => entry?.id === id);
  const pending = entries.filter((entry) => isAvailable(entry, now())).length;
  const nextClaimExpiry = entries.reduce<number | null>((next, entry) => {
    if (entry.claimedBy === null || entry.claimExpiresAt === null) return next;
    return next === null ? entry.claimExpiresAt : Math.min(next, entry.claimExpiresAt);
  }, null);

  // Claim expiry changes derived availability without producing a Yjs update.
  // Wake the UI at the first deadline so `pending` and disabled controls stay
  // accurate even when every peer is otherwise idle.
  useEffect(() => {
    if (nextClaimExpiry === null) return;
    const timeout = setTimeout(
      () => rerender((version) => version + 1),
      Math.max(1, nextClaimExpiry - now()),
    );
    return () => clearTimeout(timeout);
  }, [nextClaimExpiry, now]);

  return {
    entries,
    pending,
    enqueue: (payload, enqueueOpts) => {
      if (!array) return null;
      const id = enqueueOpts?.id ?? makeId();
      if (!id || indexOf(id) !== -1) return null;
      const entry: SharedQueueEntry<T> = {
        id,
        payload,
        enqueuedAt: now(),
        claimedBy: null,
        claimExpiresAt: null,
      };
      array.push([entry]);
      return entry;
    },
    claimNext: () => {
      if (!array || !room?.peerId) return null;
      const claimedAt = now();
      const current = currentEntries();
      const index = current.findIndex((entry) => isAvailable(entry, claimedAt));
      const entry = index < 0 ? undefined : current[index];
      if (!entry) return null;
      const claimed: SharedQueueEntry<T> = {
        ...entry,
        claimedBy: room.peerId,
        claimExpiresAt: claimedAt + ttlMs,
      };
      room.doc.transact(() => {
        array.delete(index, 1);
        array.insert(index, [claimed]);
      });
      return claimed;
    },
    acknowledge: (id) => {
      if (!array || !room?.peerId) return false;
      const index = indexOf(id);
      const entry = index < 0 ? undefined : currentEntries()[index];
      if (!entry || entry.claimedBy !== room.peerId) return false;
      array.delete(index, 1);
      return true;
    },
    release: (id) => {
      if (!array || !room?.peerId) return false;
      const index = indexOf(id);
      const entry = index < 0 ? undefined : currentEntries()[index];
      if (!entry || entry.claimedBy !== room.peerId) return false;
      const released: SharedQueueEntry<T> = {
        ...entry,
        claimedBy: null,
        claimExpiresAt: null,
      };
      room.doc.transact(() => {
        array.delete(index, 1);
        array.insert(index, [released]);
      });
      return true;
    },
    remove: (id) => {
      if (!array) return false;
      const index = indexOf(id);
      if (index < 0) return false;
      array.delete(index, 1);
      return true;
    },
  };
}
